// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Ticket Master (#276) — EventLog-ANCHORED correlation.
//
// Each EventLog entry is its own distinct event and gets its OWN row — EventLog
// entries are NEVER merged with each other (9 separate edits are 9 rows, not
// one). The Schedule [DATA] record and the `Scheduler_UpdateInvoice` TurboQ
// message that a given edit produced ATTACH to that edit's row when their DATA
// matches it — regardless of time (the queue can deliver seconds to days
// later). An edit may have no corresponding Schedule/TurboQ (then those cells
// are blank); a Schedule/TurboQ that matches no edit stands as its own row so
// nothing is hidden.
//
// Two transaction types correlate, one per direction of the data flow (#291):
//   • UpdateInvoiceScheduling — Mongo→FileMaker (Hookdeck). Matches the
//     OUTBOUND (Scheduler-UI) edit by RESULTING BUSINESS STATE (date_scheduled,
//     date_estimatedCompletion, installer, arrival_time_text), normalized across
//     collections. Among several same-state edits (retries), the nearest-in-time
//     edit wins — time is only a TIE-BREAKER among content matches.
//   • Insert or Update Invoice in MongoDB — FileMaker→Mongo (InsertFromURL).
//     Carries no business state (its param is just ActiveQueueId + invoiceNo), so
//     it matches the near-simultaneous INBOUND (FileMaker) EventLog echo BY TIME
//     (the Mongo write and the EventLog entry it triggers are the same moment).
// Every other script transaction, and any that finds no match, stands on its own
// row so nothing is hidden.
//
// v1 signature/mapping — tuned against real invoice 2542494; expected to evolve.
// Pure + tested.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const ET_TZ = "America/New_York";
const CANON = ["date_scheduled", "date_estimatedCompletion", "installer", "arrival_time_text"];
const SCHEDULER_CHANNEL = "Scheduler_UpdateInvoice";
// A FM→Mongo writeback and the EventLog echo it produces are the SAME Mongo
// write, so their timestamps are within seconds; this window only guards against
// attaching to a distant, unrelated inbound event.
const WRITEBACK_WINDOW_MS = 5 * 60 * 1000;

export function isoToEtWallMs(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  const p = new Intl.DateTimeFormat("en-US", {
    timeZone: ET_TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(d).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  let hour = parseInt(p.hour, 10);
  if (hour === 24) hour = 0;
  return Date.UTC(+p.year, +p.month - 1, +p.day, hour, +p.minute, +p.second);
}
export function fmToEtWallMs(value) {
  if (value == null) return null;
  const m = String(value).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!m) return null;
  let hour = parseInt(m[4], 10);
  const ap = m[7];
  if (ap) { const pm = ap.toUpperCase() === "PM"; if (pm && hour < 12) hour += 12; if (!pm && hour === 12) hour = 0; }
  return Date.UTC(+m[3], +m[1] - 1, +m[2], hour, +m[5], m[6] ? +m[6] : 0);
}
export function parsePayload(payload) {
  if (payload == null) return null;
  if (typeof payload === "object") return payload;
  const t = String(payload).trim();
  if (!(t.startsWith("{") || t.startsWith("["))) return null;
  try { return JSON.parse(t); } catch { return null; }
}
export function normVal(v) {
  if (v == null) return "";
  const s = String(v).trim();
  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${+m[1]}/${+m[2]}/${+m[3]}`;
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${+m[2]}/${+m[3]}/${+m[1]}`;
  return s;
}
// The Mongo→FileMaker scheduling push — UpdateInvoiceScheduling (#291). Carries
// the business state, so it content-matches an OUTBOUND edit. Falls back to the
// old TurboQ channel name for backward-compat with legacy-shaped rows/tests.
export function isSchedulerChannel(t) {
  return (
    String(t.channel || "") === SCHEDULER_CHANNEL ||
    /UpdateInvoiceScheduling/i.test(String(t.scriptName || ""))
  );
}

// The FileMaker→Mongo writeback — "Insert or Update Invoice in MongoDB"
// (InsertFromURL). Time-matches the INBOUND EventLog echo it produced (#291).
export function isMongoWriteback(t) {
  return /Insert or Update Invoice in MongoDB/i.test(String(t.scriptName || ""));
}
function isOutbound(e) {
  return String(e.source || "").trim().toLowerCase() === "scheduler-ui";
}

function turboState(t) {
  // #291 — the transaction's input is `param` (ScriptParameter); legacy TurboQ
  // rows used `payload`. Support both.
  const p = parsePayload(t.param ?? t.payload) || {};
  return CANON.reduce((a, k) => { a[k] = normVal(p[k]); return a; }, {});
}
function eventState(e) {
  const after = e.after || { ...(e.before || {}), ...(e.updateFields || {}) };
  return CANON.reduce((a, k) => { a[k] = normVal(after[k]); return a; }, {});
}
function scheduleState(s) {
  const map = { date_scheduled: s.StartDate, date_estimatedCompletion: s.EndDate, installer: s.InstallerNo, arrival_time_text: s.ArrivalTimeFrame };
  return CANON.reduce((a, k) => { a[k] = normVal(map[k]); return a; }, {});
}
function sig(state) {
  return CANON.map((k) => state[k]).join("¦");
}

/**
 * @param {{eventLog?:object[], schedule?:object[], turboq?:object[]}} lanes
 * @param {{schedulerOnly?:boolean, order?:"asc"|"desc"}} [opts]
 * @returns {Array<{sig:string, ms:number|null, kind:string, cells:{eventLog:object[],schedule:object[],turboq:object[]}}>}
 */
export function groupTimeline(lanes = {}, opts = {}) {
  const schedulerOnly = opts.schedulerOnly !== false;
  const order = opts.order === "desc" ? "desc" : "asc";
  const events = Array.isArray(lanes.eventLog) ? lanes.eventLog : [];
  const schedule = Array.isArray(lanes.schedule) ? lanes.schedule : [];
  let turbo = Array.isArray(lanes.turboq) ? lanes.turboq : [];
  // "Scheduling only" keeps the two core Scheduler↔FileMaker sync transactions
  // (the outbound push + the inbound writeback); "All scripts" keeps everything.
  if (schedulerOnly) turbo = turbo.filter((t) => isSchedulerChannel(t) || isMongoWriteback(t));

  // One row per EventLog entry — NEVER merged with another EventLog entry.
  const rows = events.map((e) => {
    const ms = isoToEtWallMs(e.timestamp);
    return {
      kind: "event", ms, sig: sig(eventState(e)), outbound: isOutbound(e),
      cells: { eventLog: [{ raw: e, ms }], schedule: [], turboq: [] },
    };
  });

  // Attach an item to the nearest-in-time EventLog row whose signature matches
  // (optionally restricted to outbound rows). Returns true if attached.
  const attach = (itemSig, ms, laneKey, requireOutbound) => {
    let best = null, bestScore = Infinity;
    for (const r of rows) {
      if (r.kind !== "event" || r.sig !== itemSig) continue;
      if (requireOutbound && !r.outbound) continue;
      const score = ms != null && r.ms != null ? Math.abs(ms - r.ms) : Infinity;
      if (score < bestScore) { bestScore = score; best = r; }
    }
    return best;
  };

  // Time-proximity attach (no content sig) — for the FM→Mongo writeback, which
  // carries no business state; it lines up with the near-simultaneous inbound
  // EventLog echo the Mongo write produced. Never crosses the window.
  const attachByTime = (ms, requireInbound, windowMs) => {
    if (ms == null) return null;
    let best = null, bestScore = Infinity;
    for (const r of rows) {
      if (r.kind !== "event" || r.ms == null) continue;
      if (requireInbound && r.outbound) continue;
      const score = Math.abs(ms - r.ms);
      if (score < bestScore) { bestScore = score; best = r; }
    }
    return bestScore <= windowMs ? best : null;
  };

  const orphans = [];
  for (const t of turbo) {
    const ms = fmToEtWallMs(t.created);
    // Dispatch by transaction type (#291):
    //   • UpdateInvoiceScheduling → OUTBOUND edit, by business state
    //   • Insert/Update Invoice in MongoDB → INBOUND echo, by time
    //   • anything else → its own row (never mis-attached)
    let row = null;
    if (isSchedulerChannel(t)) row = attach(sig(turboState(t)), ms, "turboq", true);
    else if (isMongoWriteback(t)) row = attachByTime(ms, true, WRITEBACK_WINDOW_MS);
    if (row) row.cells.turboq.push({ raw: t, ms });
    else orphans.push({ kind: "turbo", laneKey: "turboq", raw: t, ms });
  }
  for (const s of schedule) {
    const ms = fmToEtWallMs(s.CreationTimestamp);
    const row = attach(sig(scheduleState(s)), ms, "schedule", false);
    if (row) row.cells.schedule.push({ raw: s, ms });
    else orphans.push({ kind: "sched", laneKey: "schedule", raw: s, ms });
  }
  // Unmatched Schedule/TurboQ stand as their own rows (nothing hidden).
  for (const o of orphans) {
    const cells = { eventLog: [], schedule: [], turboq: [] };
    cells[o.laneKey].push({ raw: o.raw, ms: o.ms });
    rows.push({ kind: o.kind, ms: o.ms, sig: `orphan-${o.kind}-${o.ms}`, cells });
  }

  for (const r of rows) {
    for (const k of ["eventLog", "schedule", "turboq"]) {
      r.cells[k].sort((a, b) => (a.ms ?? Infinity) - (b.ms ?? Infinity));
    }
  }
  rows.sort((a, b) => {
    const am = a.ms ?? Infinity, bm = b.ms ?? Infinity;
    return order === "desc" ? bm - am : am - bm;
  });
  return rows;
}

export const __testables__ = { CANON, sig, turboState, eventState, scheduleState, isOutbound };
